import React, { useMemo, useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Avatar,
  IconButton
} from "@mui/material";
import UIWrapper from "../../components/UIWrapper";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatDate, formatDateTime } from "../../components/helper/utils";
import ShipmentInquiriesService from "../../services/ShipmentInquiriesService";
import { snackbarActions } from "../../Store/snackbar";
import { useDispatch } from "react-redux";

const badgeSx = {
  fontSize: "11px",
  fontWeight: 600,
  borderRadius: "999px",
  height: "24px",
};

const labelSx = {
  fontSize: "11px",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
};

const valueSx = {
  fontSize: "15px",
  color: "#111827",
  fontWeight: 500,
  mt: 0.4,
};

const ApproveReject = () => {
  const history = useHistory();
  const location = useLocation();
  const [comment, setComment] = useState("");
  const [newComment, setNewComment] = useState("");
  const [activityItems, setActivityItems] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [userType, setUserType] = useState("");
  const [commentError, setCommentError] = useState(false);
  const dispatch = useDispatch();


  const request = location?.state?.request || {};



const getStatusChip = (status) => {
  const map = {
    pending: { color: "#1e88e5", bg: "#e3f2fd" },
    "approved & processing": { color: "#2e7d32", bg: "#e8f5e9" },
    shipped: { color: "#7c3aed", bg: "#ede9fe" },
    delivered: { color: "#2e7d32", bg: "#e8f5e9" },
    cancelled: { color: "#d32f2f", bg: "#fdecea" },
    rejected: { color: "#d32f2f", bg: "#fdecea" },
    draft: { color: "#757575", bg: "#f3f4f6" },
  };

  const key = status?.toLowerCase();
  const item = map[key] || { color: "#1976d2", bg: "#e3f2fd" };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        px: "10px",
        py: "4px",
        borderRadius: "999px",
        backgroundColor: item.bg,
        width: "fit-content",
      }}
    >
      <Box
        sx={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: item.color,
        }}
      />
      <Typography
        sx={{
          fontSize: "12px",
          color: item.color,
          fontWeight: 500,
        }}
      >
        {status}
      </Typography>
    </Box>
  );
};

  const commonDispatch = (type, message) => {
    dispatch(
      snackbarActions.showNotification({
        snackbarOpen: true,
        snackbarType: type,
        snackbarMessage: message,
      })
    );
  };

  const handleApprove = () => {
    const payload = {
      request_id: request.request_id,
      status: "Approved & Processing",
      comment: comment
    };



    ShipmentInquiriesService.approveRejectShipmentRequest(payload)
      .then(() => {
        commonDispatch("success", "Request approved successfully");
        history.push("/sample-distribution/shipment_request");
      })
      .catch((err) => {
        commonDispatch("error", err.message);
      })

  };

  const handleReject = () => {
    if (!comment.trim()) {
      setCommentError(true);
      return;
    }

    const payload = {
      request_id: request.request_id,
      status: "Rejected",
      comment: comment
    };

    ShipmentInquiriesService.approveRejectShipmentRequest(payload)
      .then(() => {
        commonDispatch("error", "Request rejected");
        history.push("/sample-distribution/shipment_request");
      })
      .catch((err) => {
        commonDispatch("error", err.message);
      });
  };


  const headerData = useMemo(
    () => ({
      requestId: request.request_id,
      submittedBy: request.submitted_by,
      submittedDate: formatDate(request.created_on),
      sampleCount: request.samples,
      destination: request.destination_site,
      shipDate: request.ship_date,
      carrier: request.carrier,
      temperature: request.temprature,
      shippingAddress:
        request.shipping_address,
      specialInstructions:
        request.special_instruction,
      totalVolume: request.total_volume,
      status: getStatusChip(request.status),
    }),
    [request]
  );

  useEffect(() => {
    ShipmentInquiriesService.getRequestDetails({ request_id: request.request_id })
      .then((resp) => {
        const data = resp.data;

        setUserType(data.user_type);
        setSampleRows(data.data || []);
      })
      .catch((err) => {
        console.log("Error loading request details:", err);
      });
  }, []);

  useEffect(() => {
    loadComments();
  }, []);

  const loadComments = () => {
    ShipmentInquiriesService.getRequestComments({ request_id: request.request_id })
      .then((resp) => {
        const grouped = resp.data.reduce((acc, c) => {
          const date = formatDate(c.created_on.split(" ")[0]);

          if (!acc[date]) acc[date] = [];

          acc[date].push({
            id: c.id,
            user: c.posted_by,
            avatar: c.posted_by.charAt(0).toUpperCase(),
            time: formatDateTime(c.created_on),
            content: c.comment
          });

          return acc;
        }, {});



        setActivityItems(grouped);
      })
      .catch((err) => console.log(err));
  };

  const handlePostComment = async () => {
    const formData = new FormData();
    formData.append("request_id", request.request_id);
    formData.append("comment", newComment);

    try {
      await ShipmentInquiriesService.submitRequestComments(formData);
      setNewComment("");
      loadComments();
      commonDispatch("success", "Comment posted successfully");
    } catch (err) {
      console.log("Error posting comment:", err);
      commonDispatch("error", err.message || "Failed to post comment");
    }
  };

  return (
    <UIWrapper paper={false}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: { xs: 3, lg: 0 },
        }}
      >
        <Box
          sx={{
            pr: { lg: 3 },
            flex: {
              xs: "1 1 100%",
              lg: "0 0 70%",
            },
            maxWidth: {
              xs: "100%",
              lg: "70%",
            },
          }}
        >
          <Box
            sx={{
              borderRadius: "12px",
              background: "#fff",
              border: "1px solid #e5e7eb",
              overflow: "hidden",
            }}
          >
            <Box sx={{ borderBottom: "1px solid #e5e7eb" }}>
              <Box sx={{ borderBottom: "1px solid #e5e7eb" }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box sx={{ p: 3, pb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#111827" }}>
                        {headerData.requestId}
                      </Typography>
                      {headerData.status}
                      <Chip 
                        label="YOU'RE REVIEWING AS INTERNAL APPROVER"
                        sx={{
                          ...badgeSx,
                          bgcolor: "#eef2ff",
                          color: "#4338ca",
                          fontSize: "10px",
                        }}
                      />
                    </Stack>

                    <Typography sx={{ fontSize: "16px", color: "#4b5563", fontWeight: 400 }}>
                      Submitted by {headerData.submittedBy} on {headerData.submittedDate} ·{" "}
                      {headerData.sampleCount} samples · {headerData.destination}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box sx={{ p: 3, pt: 2 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={3}
                  justifyContent="space-between"
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Destination</Typography>
                    <Typography sx={valueSx}>{headerData.destination}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Ship Date</Typography>
                    <Typography sx={valueSx}>{headerData.shipDate}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Carrier</Typography>
                    <Typography sx={valueSx}>{headerData.carrier}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Temperature</Typography>
                    <Typography sx={valueSx}>{headerData.temperature}</Typography>
                  </Box>
                </Stack>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={3}
                  justifyContent="space-between"
                  sx={{ mt: 2.5 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Shipping Address</Typography>
                    <Typography sx={valueSx}>{headerData.shippingAddress}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Special Instructions</Typography>
                    <Typography sx={valueSx}>{headerData.specialInstructions}</Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>

            <Box sx={{ pt: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1, borderBottom: "1px solid #e5e7eb", px: 3 }}>
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>
                  Requested Components
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                  Total volume: {headerData.totalVolume}
                </Typography>
              </Stack>

              <TableContainer 
              sx={{
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
              >
                <Table stickyHeader size="small">
                  <TableHead sx={{ bgcolor: "#f9fafb" }}>
                    <TableRow>
                      <TableCell>INVENTORY CODE</TableCell>
                      <TableCell>SAMPLE TYPE</TableCell>
                      <TableCell>CONTAINER</TableCell>
                      <TableCell>VOLUME</TableCell>
                      <TableCell>SITE</TableCell>
                      <TableCell>STATUS</TableCell>
                      <TableCell>SERVICE STATUS</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {sampleRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align="center"
                          sx={{ py: 3, color: "#6b7280", fontSize: "14px" }}
                        >
                          No Records Found
                        </TableCell>
                      </TableRow>
                    ) : (
                      sampleRows.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{row.inventory_code}</TableCell>
                          <TableCell>{row.sample_type}</TableCell>
                          <TableCell>{row.container_type}</TableCell>
                          <TableCell>{row.volume}</TableCell>
                          <TableCell>{row.site}</TableCell>

                          <TableCell>
                            <Chip
                              label={row.status}
                              sx={{
                                fontSize: "12px",
                                bgcolor: "#fff7e6",
                                color: "#b45309",
                                borderRadius: "999px",
                                height: "24px",
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            {row.service_status}

                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>

                </Table>
              </TableContainer>

              {userType === "internal" && (
              <Box
                sx={{ p: 3, }}
              >
                <Box sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                }}>
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >

                    <Typography sx={{ fontSize: "15px", fontWeight: 600, }}>
                      <IconButton color="yellow">
                        <InfoOutlinedIcon />
                      </IconButton>
                      Awaiting your decision
                    </Typography>
                    <Typography sx={{ fontSize: "12px", mt: 0.3, pl: 4 }}>
                      Review the requested components, leave a comment, then approve or reject.
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      value={comment}
                      onChange={(e) => {
                        setComment(e.target.value);
                        setCommentError(false);
                      }}
                      placeholder="Add your comment..."
                      error={commentError}
                      helperText={commentError ? "Comment is required to reject the request" : ""}
                      sx={{
                        mb: 1.5,
                        "& .MuiInputBase-root.Mui-disabled": {
                          cursor: "not-allowed",
                          bgcolor: "#f9fafb",
                        },
                        "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled": {
                          cursor: "not-allowed"
                        }
                      }}
                      disabled={request?.status === "Approved" || request?.status === "Rejected" || request?.status === "Approved & Processing"}
                    />
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Button variant="outlined" color="error" sx={{ fontSize: "12px" }} onClick={handleReject} disabled={request?.status === "Approved" || request?.status === "Rejected" || request?.status === "Approved & Processing"}>
                          REJECT
                        </Button>

                        <Button variant="contained" color="success" sx={{ fontSize: "12px" }} onClick={handleApprove} disabled={request?.status === "Approved" || request?.status === "Rejected" || request?.status === "Approved & Processing"}>
                          APPROVE
                        </Button>
                      </Stack>
                    

                  </Box>
                </Box>
              </Box>
              )}
            </Box>
          </Box>
        </Box>
        {/* Right Sidebar - Activity & Comments */}
        <Box
          sx={{
            flex: {
              xs: "1 1 100%",
              lg: "0 0 30%",
            },
            maxWidth: {
              xs: "100%",
              lg: "30%",
            },
            border: "1px solid #e5e7eb",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
            mt: { xs: 3, lg: 0 },
          }}
        >

          {/* Header */}
          <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>
              Activity & Comments
            </Typography>
          </Box>

          {/* Activity Feed */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#d1d5db",
                borderRadius: "3px",
              },
            }}
          >
            <Stack spacing={2} sx={{ height: "100%" }}>
              {Object.keys(activityItems).length === 0 &&
                !(request.status === "Approved" || request.status === "Approved & Processing" || request.status === "Rejected") ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "15px", color: "#6b7280", fontWeight: 500 }}>
                    No activity & comments found
                  </Typography>
                </Box>
              ) : (
                Object.keys(activityItems).map((date) => (
                  <Box key={date} sx={{ mb: 2 }}>
                    {/* Date Heading */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box sx={{ flex: 1, height: "1px", bgcolor: "#d1d5db" }} />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#6b7280",
                          mx: 2,
                          textAlign: "center",
                        }}
                      >
                        {date}
                      </Typography>
                      <Box sx={{ flex: 1, height: "1px", bgcolor: "#d1d5db" }} />
                    </Box>

                    {/* Comments */}
                    {activityItems[date].map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          py: 1,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: "12px",
                            fontWeight: 600,
                            bgcolor:
                              item.role === "REQUESTOR" ? "#dbeafe" : "#f0fdf4",
                            color:
                              item.role === "REQUESTOR" ? "#0369a1" : "#15803d",
                          }}
                        >
                          {item.avatar}
                        </Avatar>

                        <Box
                          sx={{
                            flex: 1,
                            p: 2,
                            borderRadius: "8px",
                            border: "1px solid #e5e7eb",
                            background: "#f9f9f9",
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ fontSize: "13px" }}>
                              {item.user}
                            </Typography>

                            <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                              {item.time}
                            </Typography>
                          </Stack>

                          <Typography sx={{ fontSize: "13px", mt: 0.8 }}>
                            {item.content}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ))
              )}
              { /* Status-based system message */}

              {(request.status === "Approved" || request.status === "Approved & Processing") && (
                <Box sx={{ mt: 2, p: 2, borderRadius: "8px", border: "1px solid #e5e7eb", background: "#f0fdf4" }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#15803d" }}>
                    This shipment request {request.request_id} is approved by {request.action_by}.
                  </Typography>
                </Box>
              )}

              {request.status === "Rejected" && (
                <Box sx={{ mt: 2, p: 2, borderRadius: "8px", border: "1px solid #e5e7eb", background: "#fee2e2" }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#b91c1c" }}>
                    This shipment request {request.request_id} is rejected by {request.action_by}.
                  </Typography>
                </Box>
              )}
            </Stack>



          </Box>


          {/* Comment Input */}
          <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
            <TextField
              fullWidth
              placeholder="Add a comment..."
              multiline
              minRows={2}
              maxRows={4}
              size="small"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              disabled={request.status === "Approved" || request.status === "Approved & Processing" || request.status === "Rejected"}
              sx={{
                "& .MuiInputBase-root.Mui-disabled": {
                  cursor: "not-allowed",
                  bgcolor: "#f9fafb",
                },
                "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled": {
                  cursor: "not-allowed"
                }
              }}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handlePostComment}
                disabled={!newComment.trim() || request.status === "Approved" || request.status === "Approved & Processing" || request.status === "Rejected"}
              >
                Post Comment
              </Button>
            </Stack>
          </Box>

        </Box>
      </Box>

    </UIWrapper>


  );
};

export default ApproveReject;
